import { useState } from "react";
import { ChevronLeft, ChevronRight, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { fmtDateHeure } from "@/components/scans/common";
import { API, api, errMsg } from "@/lib/api";

export const urlPhoto = (colisId, p, mini = false) => `${API}/colis/${colisId}/photos/${p.id}${mini ? "?mini=true" : ""}`;

/** Grille de miniatures ; un clic ouvre la photo en grand (défilement,
 * téléchargement, suppression pour l'auteur ou un administrateur). */
export function PhotosGrille({ colis, photos, user, onChange, size = "md" }) {
  const [idx, setIdx] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const cur = idx != null ? photos[idx] : null;
  const instLabel = (p) => (colis.instances || []).find((i) => i.id === p.instance_id)?.motif_label;
  const peutSupprimer = (p) => user?.is_admin || p.par?.matricule === user?.matricule;

  const supprimer = async () => {
    try {
      await api.delete(`/colis/${colis.id}/photos/${cur.id}`);
      toast.success("Photo supprimée");
      setIdx(null); setConfirm(false);
      onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const aller = (d) => { setConfirm(false); setIdx((i) => (i + d + photos.length) % photos.length); };

  if (!photos.length) return null;
  return (
    <>
      <div className={`grid gap-2 ${size === "sm" ? "grid-cols-5 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-4"}`}>
        {photos.map((p, i) => (
          <button key={p.id} type="button" onClick={() => { setIdx(i); setConfirm(false); }} className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted hover:opacity-90" data-testid={`photo-${p.id}`}>
            <img src={urlPhoto(colis.id, p, true)} alt={p.legende || "Photo du colis"} loading="lazy" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      <Dialog open={!!cur} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent className="sm:max-w-3xl p-3 sm:p-4" data-testid="photo-viewer">
          {cur && (
            <>
              <DialogTitle className="text-sm pr-8">{instLabel(cur) ? `Instance : ${instLabel(cur)}` : "Photo du colis"}{cur.legende ? ` — ${cur.legende}` : ""}</DialogTitle>
              <DialogDescription className="text-xs">{fmtDateHeure(cur.ajoutee_le)} · {cur.par?.nom || "—"} · {idx + 1}/{photos.length}</DialogDescription>
              <div className="relative bg-black rounded-md overflow-hidden flex items-center justify-center min-h-[40vh]">
                <img src={urlPhoto(colis.id, cur)} alt="" className="max-h-[70dvh] w-auto object-contain" />
                {photos.length > 1 && (
                  <>
                    <button type="button" onClick={() => aller(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white" aria-label="Précédente"><ChevronLeft size={18} /></button>
                    <button type="button" onClick={() => aller(1)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white" aria-label="Suivante"><ChevronRight size={18} /></button>
                  </>
                )}
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <a href={urlPhoto(colis.id, cur)} download={`colis-${colis.numero}-${idx + 1}.jpg`} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-xs hover:bg-accent"><Download size={13} /> Télécharger</a>
                {peutSupprimer(cur) && (confirm
                  ? <Button size="sm" variant="destructive" onClick={supprimer} data-testid="photo-supprimer-ok">Supprimer la photo ?</Button>
                  : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirm(true)}><Trash2 size={13} /> Supprimer</Button>)}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
